import React, {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useRef,
  useState,
} from "react";
import { CircleAlert, X } from "lucide-react";
import { MGField, TMGFieldProps, useFieldId } from "../MGField/MGField";
import fieldStyles from "../MGField/MGField.module.css";
import styles from "./MGInput.module.css";

export type TMGInputProps = TMGFieldProps &
  Omit<
    InputHTMLAttributes<HTMLInputElement>,
    keyof TMGFieldProps | "defaultValue" | "height" | "onChange" | "value"
  > & {
    allowClearWithoutFocus?: boolean;
    clearAriaLabel?: string;
    dataTestId?: string;
    defaultValue?: string;
    height?: number | string;
    inputClassName?: string;
    leftIcon?: ReactNode;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onClear?: () => void;
    onValueChange?: (value: string) => void;
    rightIcon?: ReactNode;
    rightIconAriaLabel?: string;
    rightIconOnClick?: MouseEventHandler<HTMLButtonElement>;
    value?: string;
  };

export const MGInput = forwardRef<HTMLInputElement, TMGInputProps>((props, forwardedRef) => {
  const {
    id: externalId,
    allowClearWithoutFocus = false,
    background,
    className,
    clearAriaLabel = "Limpar campo",
    dataTestId,
    defaultValue = "",
    disabled = false,
    error = false,
    errorText,
    height,
    helperText,
    inputClassName,
    labelText,
    leftIcon,
    onBlur,
    onChange,
    onClear,
    onFocus,
    onValueChange,
    readOnly = false,
    required = false,
    rightIcon,
    rightIconAriaLabel = "Ação do campo",
    rightIconOnClick,
    showLabels,
    style,
    type = "text",
    value,
    "aria-describedby": externalDescribedBy,
    ...inputProps
  } = props;
  const id = useFieldId(externalId);
  const controlled = Object.prototype.hasOwnProperty.call(props, "value");
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const currentValue = controlled ? (value ?? "") : internalValue;
  const filled = currentValue.length > 0;
  const locked = disabled || readOnly;
  const message = (error && errorText) || helperText;
  const describedBy =
    [message && `${id}-message`, externalDescribedBy].filter(Boolean).join(" ") || undefined;
  const showClear = filled && !locked && !!onClear && (allowClearWithoutFocus || focused);
  const showWarning = error && !focused && !locked && !showClear;
  const controlStyle = height
    ? { minHeight: typeof height === "number" ? `${height}px` : height }
    : undefined;

  const setInputRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.currentTarget.value;
    if (!controlled) setInternalValue(nextValue);
    onChange?.(event);
    onValueChange?.(nextValue);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (!event.currentTarget.parentElement?.contains(event.relatedTarget as Node)) {
      setFocused(false);
    }
    onBlur?.(event);
  };

  const clear = () => {
    if (!controlled) setInternalValue("");
    onValueChange?.("");
    onClear?.();
    inputRef.current?.focus();
  };

  const focusFromControl = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!locked && !target.closest("input, button")) {
      event.preventDefault();
      inputRef.current?.focus();
    }
  };

  return (
    <MGField
      id={id}
      labelText={labelText}
      helperText={helperText}
      error={error}
      errorText={errorText}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      showLabels={showLabels}
      background={background}
      className={className}
      style={style}
      dataTestId={dataTestId}
      filled={filled}>
      <div className={styles.controlRow}>
        <div className={fieldStyles.control} style={controlStyle} onMouseDown={focusFromControl}>
          {leftIcon && (
            <span className={styles.leftIcon} aria-hidden="true">
              {leftIcon}
            </span>
          )}
          <input
            {...inputProps}
            ref={setInputRef}
            id={id}
            type={type}
            className={`${fieldStyles.input}${inputClassName ? ` ${inputClassName}` : ""}`}
            value={currentValue}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={error || undefined}
            aria-describedby={describedBy}
            data-testid={dataTestId && `${dataTestId}-input`}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {showClear && (
            <button
              type="button"
              className={fieldStyles.iconButton}
              aria-label={clearAriaLabel}
              title={clearAriaLabel}
              data-testid={dataTestId && `${dataTestId}-clear`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={clear}>
              <X size={24} aria-hidden="true" />
            </button>
          )}
          {showWarning && (
            <CircleAlert
              className={`${fieldStyles.icon} ${fieldStyles.errorIcon}`}
              data-testid={dataTestId && `${dataTestId}-warning`}
              aria-hidden="true"
            />
          )}
        </div>
        {rightIcon &&
          !readOnly &&
          (rightIconOnClick ? (
            <button
              type="button"
              className={styles.rightIconButton}
              disabled={disabled}
              aria-label={rightIconAriaLabel}
              title={rightIconAriaLabel}
              data-testid={dataTestId && `${dataTestId}-right-icon`}
              onClick={rightIconOnClick}>
              {rightIcon}
            </button>
          ) : (
            <span className={styles.rightIcon} aria-hidden="true">
              {rightIcon}
            </span>
          ))}
      </div>
    </MGField>
  );
});

MGInput.displayName = "MGInput";
